/**
 * JSON messages (docs §3 "Message schema"). Lobby traffic, intents, acks and match
 * lifecycle are JSON text frames; snapshots are binary frames (see codec.ts).
 *
 * Client → server messages are validated strictly by `parseClientMessage`: exact key sets
 * (unknown keys rejected), types, integer / range checks. The server must still run
 * `validateIntent` for game rules; this layer only guarantees a well-formed shape.
 */
import type {
  AircraftKind,
  AirMission,
  BotDifficulty,
  BuildKind,
  EndReason,
  Intent,
  IntentError,
  MatchMode,
  MatchSettings,
  MatchStats,
  PlayerId,
  PlayerInfo,
  PlayerSlot,
  RegionId,
  Standing,
  TeamId,
  Tick,
} from '@sa/sim';
import type { Chronicle } from './chronicle.js';
import { CHAT_CHANNELS, cleanChatText, type ChatErrorMessage, type ChatMessage, type ChatSend } from './chat.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max intents per second per connection (§3). */
export const INTENT_RATE_LIMIT = 20;
/** Text frames longer than this are rejected before JSON.parse. */
export const MAX_CLIENT_MESSAGE_BYTES = 2048;
/** Private room codes: 5 characters without look-alikes (no B/8, S/5, Z/2, U/V, L/1, O/0, I). */
export const ROOM_CODE_ALPHABET = 'ACDEFGHJKMNPQRTWXY34679';
export const ROOM_CODE_LENGTH = 5;
const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);

// ---------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------

export type LobbyMessage =
  /** `resumeToken` (from a previous `welcome`) reclaims a held seat after a reconnect. */
  | { type: 'auth'; ticket: string; resumeToken?: string }
  | {
      type: 'room.create';
      mode: MatchMode;
      maxPlayers: number;
      teamCount?: number;
      private: boolean;
      /** dev only (the server ignores it in production): simulation speed multiplier */
      simSpeed?: number;
      /** dev only: match length override in ticks */
      matchTicks?: number;
    }
  | { type: 'room.join'; code: string }
  | { type: 'quickplay.join' }
  | { type: 'quickplay.leave' }
  /** host only */
  | { type: 'room.setSlot'; slot: number; kind: 'open' | 'closed' | 'bot'; difficulty?: BotDifficulty; team?: TeamId }
  | { type: 'room.pickRegion'; regionId: RegionId | null }
  | { type: 'room.ready'; ready: boolean }
  | { type: 'room.start' }
  | { type: 'room.leave' };

/** In-match intent messages: `in.<Intent type>` with the intent's fields plus `seq`. */
export type IntentMessage = {
  [K in Intent['type']]: { type: `in.${K}`; seq: number } & Omit<Extract<Intent, { type: K }>, 'type'>;
}[Intent['type']];

export type PingMessage = { type: 'ping'; t: number };

export type ClientMessage = LobbyMessage | IntentMessage | PingMessage | ChatSend;
export type ClientMessageType = ClientMessage['type'];

// ---------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------

export type RoomErrorCode =
  | 'badMessage'
  | 'unauthorized'
  | 'rateLimited'
  | 'notFound'
  | 'full'
  | 'notHost'
  | 'notReady'
  | 'badSlot'
  | 'regionTaken'
  | 'alreadyInRoom'
  | 'notInRoom'
  | 'inMatch'
  | 'resumeExpired'
  | 'badTeam'
  | 'badRegion'
  | 'notEnoughPlayers'
  | 'oneTeam';

export type RoomPhase = 'lobby' | 'countdown' | 'inMatch';
export type RoomLeftReason = 'kicked' | 'left' | 'closed';

export type AckError = IntentError | 'rateLimited' | 'notInMatch';

/** Player row in `welcome` (no account ids of other players). */
export type WelcomePlayer = Pick<PlayerInfo, 'id' | 'name' | 'team' | 'regionId' | 'isBot' | 'difficulty'>;

export interface RoomStateMessage {
  type: 'room.state';
  code: string;
  hostId: string;
  slots: PlayerSlot[];
  settings: MatchSettings;
  /**
   * Start regions this lobby plays on (the curated set for its expected player count, §1.14;
   * all regions for 8). `room.pickRegion` only accepts these; the lobby map highlights them.
   */
  regionSet: RegionId[];
  /**
   * Teams mode with a measured team layout (§1.14): the regions each team plays (index = team − 1).
   * Regions are then fixed by team and `room.pickRegion` is refused.
   */
  teamRegions?: RegionId[][];
  quickplay: boolean;
  phase: RoomPhase;
  /** quick-play: epoch ms when the room fills with bots and starts */
  fillAt?: number;
}

export interface WelcomeMessage {
  type: 'welcome';
  matchId: string;
  you: { playerId: PlayerId; team: TeamId };
  players: WelcomePlayer[];
  settings: MatchSettings;
  mapVersion: string;
  tickRate: number;
  tick: Tick;
  resumeToken: string;
}

export interface EndMessage {
  type: 'end';
  reason: EndReason;
  winners: PlayerId[];
  standings: Standing[];
  stats: MatchStats;
  /** the story of the match (chronicle.ts): the same for every player, fog lifted */
  chronicle?: Chronicle;
}

export type ServerMessage =
  | RoomStateMessage
  | { type: 'room.error'; code: RoomErrorCode; msg: string }
  | { type: 'room.left'; reason: RoomLeftReason }
  | { type: 'match.starting'; matchId: string; countdownMs: number }
  | WelcomeMessage
  | { type: 'ack'; seq: number; ok: boolean; err?: AckError }
  | { type: 'pong'; t: number; tick: Tick }
  | EndMessage
  | ChatMessage
  | ChatErrorMessage;
export type ServerMessageType = ServerMessage['type'];

const SERVER_TYPES: ReadonlySet<string> = new Set<ServerMessageType>(['room.state', 'room.error', 'room.left', 'match.starting', 'welcome', 'ack', 'pong', 'end', 'chat', 'chat.error']);

// ---------------------------------------------------------------------------
// Strict validation of client messages
// ---------------------------------------------------------------------------

type Check = (v: unknown) => boolean;
interface Field {
  check: Check;
  optional?: boolean;
}
type Shape = Record<string, Field>;

const U32 = 0xffff_ffff;
const COORD_MAX = 1000;

const int = (lo: number, hi: number): Check => (v) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const num = (lo: number, hi: number): Check => (v) => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
const str = (min: number, max: number): Check => (v) => typeof v === 'string' && v.length >= min && v.length <= max;
const bool: Check = (v) => typeof v === 'boolean';
const oneOf = (...xs: readonly string[]): Check => {
  const s = new Set(xs);
  return (v) => typeof v === 'string' && s.has(v);
};
const nullable = (c: Check): Check => (v) => v === null || c(v);

const req = (check: Check): Field => ({ check });
const opt = (check: Check): Field => ({ check, optional: true });

const seq = req(int(0, U32));
const id = req(int(1, U32));
const hex = int(0, 0xffff);
const coord = req(num(-COORD_MAX, COORD_MAX));
const pid = req(int(1, 8));
/** a map point (chat `loc`): exactly { x, y } */
const point: Check = (v) =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 2 && num(-COORD_MAX, COORD_MAX)((v as { x?: unknown }).x) && num(-COORD_MAX, COORD_MAX)((v as { y?: unknown }).y);

const BUILD_KINDS: readonly BuildKind[] = ['radar', 'sam', 'silo', 'city', 'airbase', 'sub', 'carrier'];
const AIRCRAFT_KINDS: readonly AircraftKind[] = ['fighter', 'bomber'];
const MISSIONS: readonly AirMission[] = ['strike', 'patrol', 'scout'];
const DIFFICULTIES: readonly BotDifficulty[] = ['easy', 'normal', 'hard'];

const SHAPES: Record<ClientMessageType, Shape> = {
  auth: { ticket: req(str(1, 512)), resumeToken: opt(str(1, 512)) },
  'room.create': {
    mode: req(oneOf('ffa', 'teams')),
    maxPlayers: req(int(2, 8)),
    teamCount: opt(int(2, 4)),
    private: req(bool),
    simSpeed: opt(num(0.1, 100)),
    matchTicks: opt(int(10, 1_000_000)),
  },
  'room.join': { code: req((v) => typeof v === 'string' && ROOM_CODE_RE.test(v)) },
  'quickplay.join': {},
  'quickplay.leave': {},
  'room.setSlot': {
    slot: req(int(0, 7)),
    kind: req(oneOf('open', 'closed', 'bot')),
    difficulty: opt(oneOf(...DIFFICULTIES)),
    team: opt(int(1, 8)),
  },
  'room.pickRegion': { regionId: req(nullable(int(1, 8))) },
  'room.ready': { ready: req(bool) },
  'room.start': {},
  'room.leave': {},
  'in.focus': { seq, hex: req(nullable(hex)) },
  'in.build': { seq, kind: req(oneOf(...BUILD_KINDS)), hex: req(hex) },
  'in.cancel': { seq, unitId: id },
  'in.upgradeCity': { seq, unitId: id },
  'in.buyMissile': { seq, siloId: id, count: req(int(1, 8)) },
  'in.launch': { seq, launcherId: id, x: coord, y: coord },
  'in.buildAircraft': { seq, baseId: id, kind: req(oneOf(...AIRCRAFT_KINDS)) },
  'in.air': { seq, baseId: id, kind: req(oneOf(...AIRCRAFT_KINDS)), mission: req(oneOf(...MISSIONS)), x: coord, y: coord },
  'in.airCmd': { seq, aircraftId: id, cmd: req(oneOf('recall', 'retarget')), x: opt(num(-COORD_MAX, COORD_MAX)), y: opt(num(-COORD_MAX, COORD_MAX)) },
  'in.move': { seq, unitId: id, x: coord, y: coord, queue: opt(bool) },
  'in.autoScramble': { seq, baseId: id, on: req(bool) },
  'in.ally.request': { seq, to: pid },
  'in.ally.respond': { seq, from: pid, accept: req(bool) },
  'in.ally.break': { seq, with: pid },
  'in.ceasefire.request': { seq, to: pid },
  'in.ceasefire.respond': { seq, from: pid, accept: req(bool) },
  'in.ceasefire.break': { seq, with: pid },
  'in.gift': { seq, to: pid, amount: req(int(1, 1_000_000)) },
  'in.mark': { seq, target: pid },
  'in.brace': { seq, cityId: id },
  'in.samPriority': { seq, samId: id, targetId: req(nullable(int(1, U32))) },
  'in.samMode': { seq, samId: id, silent: req(bool) },
  'in.surrender': { seq },
  ping: { t: req(num(0, Number.MAX_SAFE_INTEGER)) },
  // text: shape only here; parseClientMessage cleans it (cleanChatText) and refuses what is left empty / too long
  chat: { channel: req(oneOf(...CHAT_CHANNELS)), to: opt(int(1, 8)), text: req(str(1, 400)), loc: opt(point) },
};

const hasOwn = Object.prototype.hasOwnProperty;

/** Structural check of an already-parsed value. */
export function isClientMessage(v: unknown): v is ClientMessage {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const type = o.type;
  if (typeof type !== 'string' || !hasOwn.call(SHAPES, type)) return false;
  const shape = SHAPES[type as ClientMessageType];
  let seen = 1; // `type`
  for (const key in shape) {
    const f = shape[key];
    if (!hasOwn.call(o, key)) {
      if (f.optional) continue;
      return false;
    }
    if (!f.check(o[key])) return false;
    seen++;
  }
  // reject unknown keys (own enumerable keys beyond the shape)
  if (Object.keys(o).length !== seen) return false;
  // a private message names its one reader; the other channels name nobody
  if (type === 'chat' && (o.channel === 'pm') !== hasOwn.call(o, 'to')) return false;
  if (type === 'in.airCmd') {
    const hasX = hasOwn.call(o, 'x');
    if (hasX !== hasOwn.call(o, 'y')) return false;
    if (o.cmd === 'retarget' && !hasX) return false;
  }
  return true;
}

/** Parse a client text frame. Returns null for anything malformed. */
export function parseClientMessage(raw: string): ClientMessage | null {
  if (typeof raw !== 'string' || raw.length > MAX_CLIENT_MESSAGE_BYTES) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isClientMessage(v)) return null;
  if (v.type === 'chat') {
    const text = cleanChatText(v.text);
    if (text === null) return null;
    v.text = text;
  }
  return v;
}

export function isIntentMessage(m: ClientMessage): m is IntentMessage {
  return m.type.startsWith('in.');
}

/** `in.*` message → sim `Intent` (drops `seq`). */
export function intentOf(m: IntentMessage): Intent {
  const { type, seq: _seq, ...rest } = m;
  return { type: type.slice(3), ...rest } as Intent;
}

/** Sim `Intent` → `in.*` message with the given sequence number. */
export function intentMessage(intent: Intent, seq: number): IntentMessage {
  const { type, ...rest } = intent;
  return { type: `in.${type}`, seq, ...rest } as IntentMessage;
}

// ---------------------------------------------------------------------------
// Server messages
// ---------------------------------------------------------------------------

export const encodeMessage = (m: ServerMessage | ClientMessage): string => JSON.stringify(m);

/** Light check for client-side use (the server is trusted): known `type` on an object. */
export function parseServerMessage(raw: string): ServerMessage | null {
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof v !== 'object' || v === null) return null;
  const t = (v as { type?: unknown }).type;
  return typeof t === 'string' && SERVER_TYPES.has(t) ? (v as ServerMessage) : null;
}
